'use client';

import { PriceTicker } from '@/components/PriceTicker';
import { AccountValue } from '@/components/AccountValue';
import { EthPriceChart } from '@/components/EthPriceChart';
import { Sidebar } from '@/components/Sidebar';
import { TradingControl } from '@/components/TradingControl';
import { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';

export default function Home() {
  const [currentView, setCurrentView] = useState<'live' | 'leaderboard'>('live');
  const [showControl, setShowControl] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const { locale, setLocale, t } = useLanguage();

  return (
    <div className="flex min-h-screen flex-col bg-black text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-black">
        <div className="px-4 sm:px-6 py-3 sm:py-4">
          <div className="mx-auto flex max-w-[1920px] items-center justify-between">
            <div className="flex items-center gap-4 sm:gap-8">
              <h1 className="font-mono text-sm sm:text-lg font-bold tracking-tight">
                {t.title}
              </h1>

              <nav className="hidden sm:flex gap-6">
                <button
                  onClick={() => setCurrentView('live')}
                  className={`font-mono text-xs font-semibold uppercase tracking-wider transition-colors ${
                    currentView === 'live'
                      ? 'text-white'
                      : 'text-gray-500 hover:text-gray-300'
                  }`}
                >
                  {t.nav.live}
                </button>
                <button
                  onClick={() => setCurrentView('leaderboard')}
                  className={`font-mono text-xs font-semibold uppercase tracking-wider transition-colors ${
                    currentView === 'leaderboard'
                      ? 'text-white'
                      : 'text-gray-500 hover:text-gray-300'
                  }`}
                >
                  {t.nav.leaderboard}
                </button>
                <button
                  className="font-mono text-xs font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-300"
                >
                  {t.page.models}
                </button>
              </nav>

              {/* Mobile Navigation Toggle */}
              <button
                onClick={() => setShowMobileSidebar(!showMobileSidebar)}
                className="sm:hidden flex items-center justify-center w-8 h-8 rounded border border-white/20"
              >
                <span className="text-xs font-mono">{showMobileSidebar ? '✕' : '☰'}</span>
              </button>

              {/* Mobile Sidebar Toggle */}
              {sidebarVisible && (
                <button
                  onClick={() => setSidebarVisible(false)}
                  className="sm:hidden flex items-center justify-center w-8 h-8 rounded border border-white/20 text-xs"
                >
                  <span>☰</span>
                </button>
              )}
            </div>

            <div className="hidden sm:flex items-center gap-4">
              {/* Language Switcher */}
              <div className="flex gap-2">
                <button
                  onClick={() => setLocale('en')}
                  className={`font-mono text-xs font-semibold uppercase tracking-wider transition-colors ${
                    locale === 'en'
                      ? 'text-white'
                      : 'text-gray-500 hover:text-gray-300'
                  }`}
                >
                  EN
                </button>
                <span className="text-gray-600">|</span>
                <button
                  onClick={() => setLocale('zh')}
                  className={`font-mono text-xs font-semibold uppercase tracking-wider transition-colors ${
                    locale === 'zh'
                      ? 'text-white'
                      : 'text-gray-500 hover:text-gray-300'
                  }`}
                >
                  中文
                </button>
              </div>

              <button className="font-mono text-xs font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-300">
                {t.joinWaitlist} ↗
              </button>

              {/* Control Toggle */}
              <button
                onClick={() => setShowControl(!showControl)}
                className="rounded border border-white/20 px-3 py-1.5 font-mono text-xs font-semibold uppercase tracking-wider transition-colors hover:bg-white hover:text-black"
              >
                {showControl ? `✕ ${t.page.close}` : `⚙ ${t.page.control}`}
              </button>
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          {showMobileSidebar && (
            <div className="sm:hidden mt-4 pt-4 border-t border-white/10">
              <div className="space-y-3">
                <button
                  onClick={() => {
                    setCurrentView('live');
                    setShowMobileSidebar(false);
                  }}
                  className={`block w-full text-left font-mono text-xs font-semibold uppercase tracking-wider transition-colors ${
                    currentView === 'live'
                      ? 'text-white'
                      : 'text-gray-500 hover:text-gray-300'
                  }`}
                >
                  {t.nav.live}
                </button>
                <button
                  onClick={() => {
                    setCurrentView('leaderboard');
                    setShowMobileSidebar(false);
                  }}
                  className={`block w-full text-left font-mono text-xs font-semibold uppercase tracking-wider transition-colors ${
                    currentView === 'leaderboard'
                      ? 'text-white'
                      : 'text-gray-500 hover:text-gray-300'
                  }`}
                >
                  {t.nav.leaderboard}
                </button>
                <button className="block w-full text-left font-mono text-xs font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-300">
                  {t.page.models}
                </button>
                
                {/* Mobile Language Switcher */}
                <div className="flex gap-4 pt-2">
                  <button
                    onClick={() => setLocale('en')}
                    className={`font-mono text-xs font-semibold uppercase tracking-wider transition-colors ${
                      locale === 'en'
                        ? 'text-white'
                        : 'text-gray-500 hover:text-gray-300'
                    }`}
                  >
                    EN
                  </button>
                  <span className="text-gray-600">|</span>
                  <button
                    onClick={() => setLocale('zh')}
                    className={`font-mono text-xs font-semibold uppercase tracking-wider transition-colors ${
                      locale === 'zh'
                        ? 'text-white'
                        : 'text-gray-500 hover:text-gray-300'
                    }`}
                  >
                    中文
                  </button>
                </div>

                {/* Mobile Control Toggle */}
                <button
                  onClick={() => {
                    setShowControl(!showControl);
                    setShowMobileSidebar(false);
                  }}
                  className="w-full rounded border border-white/20 px-3 py-1.5 font-mono text-xs font-semibold uppercase tracking-wider transition-colors hover:bg-white hover:text-black"
                >
                  {showControl ? `✕ ${t.page.close}` : `⚙ ${t.page.control}`}
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Control Panel (Collapsible) */}
      {showControl && (
        <div className="border-b border-white/10 bg-zinc-950">
          <div className="mx-auto max-w-[1920px] px-4 sm:px-6 py-4 sm:py-6">
            <TradingControl />
          </div>
        </div>
      )}

      {/* Price Ticker */}
      <PriceTicker />

      {/* Main Content */}
      {currentView === 'live' ? (
        <main className="flex flex-1 overflow-hidden flex-col sm:flex-row">
          {/* Left Side - Chart (70%) */}
          <div className={`overflow-y-auto ${sidebarVisible ? 'flex-1' : 'flex-auto w-full'}`}>
            {/* Show Sidebar Toggle Button (only when sidebar is hidden) */}
            {!sidebarVisible && (
              <div className="sticky top-0 z-40 border-b border-white/10 bg-black px-4 sm:px-6 py-2 sm:py-3">
                <button
                  onClick={() => setSidebarVisible(true)}
                  className="flex items-center gap-2 rounded border border-white/20 px-3 py-1.5 font-mono text-xs font-semibold uppercase tracking-wider transition-colors hover:bg-white hover:text-black"
                >
                  <span>☰</span>
                  <span>SHOW SIDEBAR</span>
                </button>
              </div>
            )}
            
            <div className="p-4 sm:p-6 space-y-6 sm:space-y-8">
              <AccountValue />
              <div className="border-t border-white/10 pt-6 sm:pt-8">
                <EthPriceChart />
              </div>
            </div>
          </div>

          {/* Right Side - Sidebar (30%) */}
          {sidebarVisible && (
            <div className="w-full sm:w-[400px] lg:w-[500px] shrink-0 border-t sm:border-t-0 sm:border-l border-white/10">
              <Sidebar onClose={() => setSidebarVisible(false)} />
            </div>
          )}
        </main>
      ) : (
        <main className="flex-1 p-4 sm:p-6">
          <div className="mx-auto max-w-[1920px]">
            <div className="rounded border border-white/10 bg-zinc-950 p-6 sm:p-8">
              <h2 className="mb-4 font-mono text-xl sm:text-2xl font-bold">{t.leaderboard.title}</h2>
              <p className="font-mono text-xs sm:text-sm text-gray-400">{t.leaderboard.description}</p>
            </div>
          </div>
        </main>
      )}

      {/* Status Bar */}
      <div className="border-t border-white/10 bg-black px-4 sm:px-6 py-2 sm:py-3">
        <div className="mx-auto flex max-w-[1920px] items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="h-1 w-16 sm:w-20 overflow-hidden rounded-full bg-zinc-800">
              <div className="h-full w-full animate-pulse bg-emerald-500"></div>
            </div>
            <span className="font-mono text-[8px] sm:text-[10px] uppercase tracking-wider text-gray-400">
              {t.status.streaming}
            </span>
          </div>
          <span className="font-mono text-[8px] sm:text-[10px] uppercase tracking-wider text-gray-600">
            {t.status.poweredBy}
          </span>
        </div>
      </div>
    </div>
  );
}
